import { useState, useEffect } from "react";
import { toast } from "sonner";
import { pu, p } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import { PrivacyCard } from "@/components/PrivacyCard";
import { VoceCard } from "@/components/VoceCard";
import { ChiamateCard } from "@/components/ChiamateCard";
import { TemaCard } from "@/components/TemaCard";
import { AccessiFornitore } from "@/components/AccessiFornitore";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, RotateCcw, Info } from "lucide-react";

export default function Impostazioni() {
  const { settings, refresh, demoMode } = useApp();
  const { t } = useI18n();
  const [form, setForm] = useState(settings);

  useEffect(() => {
    setForm(settings);
  }, [settings]);

  const f = (k) => ({
    value: form[k] ?? "",
    onChange: (e) => setForm({ ...form, [k]: e.target.value }),
    "data-testid": `settings-${k}-input`,
  });

  return (
    <div className="fade-up max-w-2xl space-y-5" data-testid="impostazioni-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("g.config")}</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("im.title")}</h1>
      </div>

      <Card className="space-y-4 p-6">
        <div>
          <Label>{t("im.name")}</Label>
          <Input className="mt-1.5" {...f("business_name")} />
          <p className="mt-1.5 text-xs text-slate-500">{t("im.nameHint")}</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>{t("im.address")}</Label>
            <Input className="mt-1.5" {...f("address")} />
          </div>
          <div>
            <Label>{t("im.vat")}</Label>
            <Input className="mt-1.5" {...f("vat")} />
          </div>
          <div>
            <Label>{t("im.phone")}</Label>
            <Input className="mt-1.5" {...f("phone")} />
          </div>
          <div>
            <Label>{t("im.cover")}</Label>
            <Input className="mt-1.5" type="number" step="0.5" {...f("cover_charge")} />
          </div>
        </div>
        <Button
          className="h-12 w-full"
          data-testid="save-settings-btn"
          onClick={async () => {
            if (!form.business_name?.trim()) return toast.error(t("im.needName"));
            await pu("/settings", { ...form, cover_charge: Number(form.cover_charge) || 0 });
            await refresh();
            toast.success(t("im.saved"));
          }}
        >
          <Save className="mr-1.5 h-4 w-4" /> {t("im.saveBtn")}
        </Button>
      </Card>

      <Card className="space-y-4 p-6" data-testid="identity-card">
        <div>
          <h3 className="font-semibold">{t("im.identity")}</h3>
          <p className="text-xs text-slate-500">{t("im.identityHint")}</p>
        </div>
        <div>
          <Label>{t("im.motto")}</Label>
          <Input className="mt-1.5" maxLength={120} placeholder={t("im.mottoPh")} {...f("motto")} />
          <p className="mt-1.5 text-xs text-slate-500">{t("im.mottoHint")}</p>
        </div>
        <div className="flex items-center gap-4">
          {form.logo && <img src={form.logo} alt="logo" className="h-16 w-16 rounded-lg border border-slate-200 object-contain p-1" data-testid="logo-preview" />}
          <div>
            <Label>{t("im.logo")}</Label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="mt-1.5 block w-full text-xs"
              data-testid="logo-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 300 * 1024) return toast.error(t("im.logoTooBig"));
                const r = new FileReader();
                r.onload = () => setForm({ ...form, logo: r.result });
                r.readAsDataURL(file);
              }}
            />
            <p className="mt-1 text-xs text-slate-500">{t("im.logoHint")}</p>
          </div>
          {form.logo && (
            <Button variant="ghost" size="sm" className="text-rose-600" data-testid="logo-remove-btn"
                    onClick={() => setForm({ ...form, logo: null })}>
              {t("im.logoRemove")}
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {[["motto_on_login", "im.mottoLogin"], ["motto_on_bill", "im.mottoBill"], ["motto_on_messages", "im.mottoMsg"]].map(([k, label]) => (
            <label key={k} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
              <span className="text-sm">{t(label)}</span>
              <Switch checked={form[k] !== false} data-testid={`switch-${k}`}
                      onCheckedChange={(v) => setForm({ ...form, [k]: v })} />
            </label>
          ))}
        </div>
        <Button
          className="h-12 w-full"
          data-testid="save-identity-btn"
          onClick={async () => {
            await pu("/settings", { ...form, cover_charge: Number(form.cover_charge) || 0 });
            await refresh();
            toast.success(t("im.saved"));
          }}
        >
          <Save className="mr-1.5 h-4 w-4" /> {t("im.saveBtn")}
        </Button>
      </Card>

      {demoMode && (
        <Card className="p-6" data-testid="demo-info-card">
          <div className="flex items-start gap-3">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
            <div>
              <h3 className="font-semibold">{t("im.demoTitle")}</h3>
              <p className="mt-1 text-sm text-slate-500">{t("im.demoText")}</p>
              <Button
                variant="outline"
                className="mt-4"
                data-testid="reset-demo-btn"
                onClick={async () => {
                  await p("/demo/reset");
                  await refresh();
                  toast.success(t("im.resetOk"));
                }}
              >
                <RotateCcw className="mr-1.5 h-4 w-4" /> {t("im.reset")}
              </Button>
            </div>
          </div>
        </Card>
      )}
      <Card className="space-y-4 p-6" data-testid="bevande-card">
        <div>
          <h3 className="font-semibold">Bevande</h3>
          <p className="text-xs text-slate-500">
            Al tavolo la sete non aspetta: quando mandi un giro, le bevande arrivano al bancone
            già in cima alla lista. Se preferisci deciderlo caso per caso, spegni qui.
          </p>
        </div>
        <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
          <span className="text-sm">Le bevande partono sempre per prime</span>
          <Switch checked={form.drinks_first !== false} data-testid="switch-drinks_first"
                  onCheckedChange={(v) => setForm({ ...form, drinks_first: v })} />
        </label>
        <Button
          className="h-12 w-full"
          data-testid="save-bevande-btn"
          onClick={async () => {
            await pu("/settings", { ...form, cover_charge: Number(form.cover_charge) || 0 });
            await refresh();
            toast.success(t("im.saved"));
          }}
        >
          <Save className="mr-1.5 h-4 w-4" /> {t("im.saveBtn")}
        </Button>
      </Card>

      <Card className="space-y-4 p-6" data-testid="stampa-card">
        <div>
          <h3 className="font-semibold">Stampante</h3>
          <p className="text-xs text-slate-500">
            Se in sala non usi la stampante, i tasti «Stampa» sparisono da comande e conti:
            resta solo quello che serve davvero, senza pulsanti inutili sotto le dita.
          </p>
        </div>
        <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
          <span className="text-sm">Nel locale usiamo una stampante</span>
          <Switch checked={form.printing_on !== false} data-testid="switch-printing_on"
                  onCheckedChange={(v) => setForm({ ...form, printing_on: v })} />
        </label>
        <Button
          className="h-12 w-full"
          data-testid="save-stampa-btn"
          onClick={async () => {
            await pu("/settings", { ...form, cover_charge: Number(form.cover_charge) || 0 });
            await refresh();
            toast.success(t("im.saved"));
          }}
        >
          <Save className="mr-1.5 h-4 w-4" /> {t("im.saveBtn")}
        </Button>
      </Card>

      <Card className="space-y-4 p-6" data-testid="close-email-card">
        <div>
          <h3 className="font-semibold">Report di chiusura via email</h3>
          <p className="text-xs text-slate-500">
            A serata archiviata ti arriva il riepilogo: incassato, metodi di pagamento, coperti,
            differenza di cassa e piatti più venduti. Utile per controllare il locale anche da casa.
          </p>
        </div>
        <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
          <span className="text-sm">Mandami il report a fine serata</span>
          <Switch checked={form.close_email !== false} data-testid="switch-close_email"
                  onCheckedChange={(v) => setForm({ ...form, close_email: v })} />
        </label>
        <div>
          <Label>Secondo indirizzo (facoltativo)</Label>
          <Input className="mt-1.5" placeholder="socio@esempio.it" {...f("close_email_extra")} />
          <p className="mt-1.5 text-xs text-slate-500">Es. il socio o il commercialista.</p>
        </div>
        <Button
          className="h-12 w-full"
          data-testid="save-close-email-btn"
          onClick={async () => {
            await pu("/settings", { ...form, cover_charge: Number(form.cover_charge) || 0 });
            await refresh();
            toast.success(t("im.saved"));
          }}
        >
          <Save className="mr-1.5 h-4 w-4" /> {t("im.saveBtn")}
        </Button>
      </Card>

      <TemaCard />
      <ChiamateCard />      <VoceCard />
      <AccessiFornitore />
      <PrivacyCard />
    </div>
  );
}
